import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../../../services/api";
import useAuthenticate from "../../../hooks/useAuthenticate";
import Navbar from "src/components/organisms/navbar";
import Footer from "src/components/organisms/footer";
import {
  PageLocationTitle,
  SummaryCard,
  SummaryValue,
  SummaryLabel,
  RankBadge,
  AthleteChip,
  ExpandRow,
  AthletesGrid,
} from "./styles";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

interface Tournament {
  id: number;
  name: string;
  slug: string;
  authorId?: number;
}

interface AthleteWithNumber {
  name: string;
  subscriptionNumber: number;
}

interface GymResult {
  gym: string;
  athleteCount: number;
  athletes: AthleteWithNumber[];
}

interface ReportData {
  gyms: GymResult[];
  summary: {
    totalGyms: number;
    totalAthletes: number;
    topGym: string;
  };
}

const AthletesPerAcademy = () => {
  const { account, getToken }: any = useAuthenticate();
  const location = useLocation();

  const isAdminRoute = location.pathname.startsWith("/admin/");
  const isManager = account?.roles?.includes("manager") === true;
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournaments, setSelectedTournaments] = useState<Tournament[]>([]);
  const [source, setSource] = useState<"brackets" | "subscriptions">("subscriptions");
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingTournaments, setLoadingTournaments] = useState(true);
  const [expandedGym, setExpandedGym] = useState<string | null>(null);
  const [searchTable, setSearchTable] = useState("");
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    if (!account) return;
    fetchTournaments();
  }, [account]);

  const fetchTournaments = async () => {
    try {
      const response = await api.get("/managers/tournaments/info", {
        headers: { Authorization: `Bearer ${getToken() || ""}` },
      });

      let data = response.data;

      // Rota /admin/ mostra todos; rota /tournament/ filtra apenas os do dono
      if (!isAdminRoute) {
        data = data.filter((t: any) => t.authorId === account?.id);
      }

      const sorted = [...data].sort((a: any, b: any) =>
        a.name.localeCompare(b.name, "pt-BR")
      );
      setTournaments(sorted);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar torneios");
    } finally {
      setLoadingTournaments(false);
    }
  };

  const fetchReport = async () => {
    if (selectedTournaments.length === 0) {
      toast.warn("Selecione ao menos um torneio");
      return;
    }

    setLoading(true);
    setExpandedGym(null);
    setSearchTable("");
    setSortField(null);
    setSortDirection("asc");
    try {
      const ids = selectedTournaments.map((t) => t.id).join(",");
      const basePath = isAdminRoute
        ? "/administrators/tournaments/report/athletes-per-academy"
        : "/managers/tournaments/report/athletes-per-academy";

      const response = await api.get(
        `${basePath}?tournaments=${ids}&source=${source}`,
        {
          headers: { Authorization: `Bearer ${getToken() || ""}` },
        }
      );

      setReportData(response.data);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao buscar dados do relatório");
    } finally {
      setLoading(false);
    }
  };

  const exportToXLSX = () => {
    if (!reportData) return;

    const wsData = reportData.gyms.map((g, i) => ({
      "#": i + 1,
      Academia: g.gym,
      "Qtd. Atletas": g.athleteCount,
      Atletas: g.athletes.map((a) => `${a.name} (${a.subscriptionNumber})`).join(", "),
    }));

    const ws = XLSX.utils.json_to_sheet(wsData);

    // Ajustar largura das colunas
    ws["!cols"] = [
      { wch: 5 },
      { wch: 35 },
      { wch: 14 },
      { wch: 100 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Atletas por Academia");
    XLSX.writeFile(wb, "atletas-por-academia.xlsx");
  };

  const toggleExpand = (gym: string) => {
    setExpandedGym(expandedGym === gym ? null : gym);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filteredGyms = (() => {
    let result = reportData?.gyms.filter((g) => {
      if (!searchTable) return true;
      const term = searchTable.toUpperCase();
      return (
        g.gym.toUpperCase().includes(term) ||
        g.athletes.some((a) => a.name.toUpperCase().includes(term) || String(a.subscriptionNumber).includes(searchTable))
      );
    });

    if (result && sortField) {
      result = [...result].sort((a, b) => {
        const dir = sortDirection === "asc" ? 1 : -1;
        switch (sortField) {
          case "gym": return a.gym.localeCompare(b.gym, "pt-BR") * dir;
          case "athleteCount": return (a.athleteCount - b.athleteCount) * dir;
          default: return 0;
        }
      });
    }

    return result;
  })();

  // Verifica se o match foi por atleta (para auto-expandir)
  const matchedByAthlete = (gym: GymResult): boolean => {
    if (!searchTable) return false;
    const term = searchTable.toUpperCase();
    if (gym.gym.toUpperCase().includes(term)) return false;
    return gym.athletes.some((a) => a.name.toUpperCase().includes(term) || String(a.subscriptionNumber).includes(searchTable));
  };

  const highlightText = (text: string, term: string) => {
    if (!term) return text;
    const idx = text.toUpperCase().indexOf(term.toUpperCase());
    if (idx === -1) return text;
    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + term.length);
    const after = text.slice(idx + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "#fef08a", borderRadius: 2, padding: "0 1px" }}>
          {match}
        </span>
        {after}
      </>
    );
  };

  const topGymCount = reportData?.gyms?.[0]?.athleteCount || 1;

  return (
    <>
      <Navbar />
      <Box sx={{ backgroundColor: "#f3f4f8", minHeight: "100vh", pb: 6 }}>
        <Container maxWidth="lg" sx={{ pt: 4 }}>
          <PageLocationTitle>Atletas por Academia</PageLocationTitle>

          {/* Filtros */}
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 3,
              borderRadius: "12px",
              border: "1px solid rgba(26,34,82,0.06)",
            }}
          >
            <Autocomplete
              multiple
              options={tournaments}
              getOptionLabel={(option) => option.name}
              value={selectedTournaments}
              onChange={(_, newValue) => setSelectedTournaments(newValue)}
              loading={loadingTournaments}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Selecione os torneios"
                  placeholder="Buscar torneio..."
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                    sx={{
                      backgroundColor: "#1a2252",
                      color: "#fff",
                      fontWeight: 500,
                      "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" },
                    }}
                  />
                ))
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mt: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#4e5672", fontSize: "13px" }}
              >
                Fonte:
              </Typography>
              <ToggleButtonGroup
                value={source}
                exclusive
                onChange={(_, val) => val && setSource(val)}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "13px",
                    px: 2,
                    borderColor: "rgba(26,34,82,0.15)",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#1a2252 !important",
                    color: "#fff !important",
                  },
                }}
              >
                <ToggleButton value="subscriptions">Inscrições</ToggleButton>
                <ToggleButton value="brackets">Chaves</ToggleButton>
              </ToggleButtonGroup>

              <Button
                variant="contained"
                onClick={fetchReport}
                disabled={loading || selectedTournaments.length === 0}
                sx={{
                  ml: "auto",
                  backgroundColor: "#1a2252",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 4,
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#243070" },
                }}
              >
                {loading ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Buscar"
                )}
              </Button>
            </Box>
          </Paper>

          {/* Summary Cards */}
          {reportData && (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  gap: 2,
                  mt: 3,
                }}
              >
                <SummaryCard>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <SummaryLabel>Total de Atletas</SummaryLabel>
                    <SummaryValue>
                      <span translate="no">{reportData.summary.totalAthletes}</span>
                    </SummaryValue>
                  </Box>
                </SummaryCard>

                <SummaryCard>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <SummaryLabel>Total de Academias</SummaryLabel>
                    <SummaryValue>
                      <span translate="no">{reportData.summary.totalGyms}</span>
                    </SummaryValue>
                  </Box>
                </SummaryCard>

                <SummaryCard>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <SummaryLabel>Academia com mais atletas</SummaryLabel>
                    <SummaryValue
                      style={{
                        fontSize:
                          reportData.summary.topGym.length > 25
                            ? "18px"
                            : reportData.summary.topGym.length > 15
                            ? "22px"
                            : "32px",
                      }}
                    >
                      {reportData.summary.topGym || "-"}
                    </SummaryValue>
                  </Box>
                </SummaryCard>
              </Box>

              {/* Barra de busca + exportar */}
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  size="small"
                  placeholder="Buscar academia ou atleta..."
                  value={searchTable}
                  onChange={(e) => setSearchTable(e.target.value)}
                  sx={{
                    flex: 1,
                    minWidth: 200,
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                  }}
                />
                <Tooltip title="Exportar para Excel">
                  <Button
                    variant="outlined"
                    onClick={exportToXLSX}
                    sx={{
                      borderColor: "#1a2252",
                      color: "#1a2252",
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: "#1a2252",
                        color: "#fff",
                      },
                    }}
                  >
                    Exportar XLSX
                  </Button>
                </Tooltip>
              </Box>

              {/* Tabela */}
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  mt: 2,
                  mb: 4,
                  borderRadius: "12px",
                  border: "1px solid rgba(26,34,82,0.06)",
                  overflow: "hidden",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: "#1a2252",
                      }}
                    >
                      {([
                        { label: "#", field: null, align: "center" as const, width: 50, display: undefined },
                        { label: "Academia", field: "gym", align: "left" as const, width: undefined, display: undefined },
                        { label: "Atletas", field: "athleteCount", align: "center" as const, width: 120, display: undefined },
                        { label: "Proporção", field: null, align: "left" as const, width: 200, display: { xs: "none", md: "table-cell" } },
                      ]).map((col) => (
                        <TableCell
                          key={col.label}
                          sx={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "12px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            py: 1.5,
                            width: col.width,
                            display: col.display,
                            "& .MuiTableSortLabel-root": { color: "#fff !important" },
                            "& .MuiTableSortLabel-root.Mui-active": { color: "#fff !important" },
                            "& .MuiTableSortLabel-icon": { color: "#fff !important", opacity: 0.7 },
                          }}
                          align={col.align}
                        >
                          {col.field ? (
                            <TableSortLabel
                              active={sortField === col.field}
                              direction={sortField === col.field ? sortDirection : "asc"}
                              onClick={() => handleSort(col.field!)}
                            >
                              {col.label}
                            </TableSortLabel>
                          ) : (
                            col.label
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredGyms && filteredGyms.length > 0 ? (
                      filteredGyms.map((gym, index) => {
                        const globalIndex =
                          reportData.gyms.findIndex((g) => g.gym === gym.gym) + 1;
                        const autoExpand = matchedByAthlete(gym);
                        const isExpanded = expandedGym === gym.gym || autoExpand;
                        const barWidth = (gym.athleteCount / topGymCount) * 100;

                        return (
                          <>
                            <ExpandRow
                              key={gym.gym}
                              $expanded={isExpanded}
                              onClick={() => toggleExpand(gym.gym)}
                            >
                              <TableCell align="center" sx={{ py: 1.2, borderBottom: isExpanded ? "none" : undefined }}>
                                <RankBadge rank={globalIndex}>
                                  {globalIndex}
                                </RankBadge>
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  color: "#1a2252",
                                  py: 1.2,
                                  borderBottom: isExpanded ? "none" : undefined,
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  {highlightText(gym.gym, searchTable)}
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: "11px",
                                      color: "#9098b0",
                                      fontWeight: 400,
                                    }}
                                  >
                                    {isExpanded ? "▲" : "▼"}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  py: 1.2,
                                  borderBottom: isExpanded ? "none" : undefined,
                                }}
                              >
                                <Chip
                                  label={gym.athleteCount}
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "13px",
                                    backgroundColor:
                                      globalIndex === 1
                                        ? "#fef3c7"
                                        : globalIndex === 2
                                        ? "#f1f5f9"
                                        : globalIndex === 3
                                        ? "#fef3e2"
                                        : "#eef0f5",
                                    color:
                                      globalIndex === 1
                                        ? "#92400e"
                                        : globalIndex === 2
                                        ? "#475569"
                                        : globalIndex === 3
                                        ? "#9a3412"
                                        : "#4e5672",
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 1.2,
                                  display: { xs: "none", md: "table-cell" },
                                  borderBottom: isExpanded ? "none" : undefined,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: 8,
                                      width: `${barWidth}%`,
                                      minWidth: 4,
                                      backgroundColor:
                                        globalIndex === 1
                                          ? "#f59e0b"
                                          : globalIndex === 2
                                          ? "#94a3b8"
                                          : globalIndex === 3
                                          ? "#cd7f32"
                                          : "#c4c9d8",
                                      borderRadius: 4,
                                      transition: "width 0.4s ease",
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "11px",
                                      color: "#9098b0",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <span translate="no">
                                      {((gym.athleteCount / reportData.summary.totalAthletes) * 100).toFixed(1)}%
                                    </span>
                                  </Typography>
                                </Box>
                              </TableCell>
                            </ExpandRow>

                            {/* Linha expandida com atletas */}
                            {isExpanded && (
                              <TableRow key={`${gym.gym}-detail`}>
                                <TableCell
                                  colSpan={4}
                                  sx={{
                                    backgroundColor: "#f7f8fb",
                                    pt: 0,
                                    pb: 2,
                                    px: 3,
                                    borderBottom: "2px solid #eef0f5",
                                  }}
                                >
                                  <AthletesGrid>
                                    {gym.athletes.map((athlete) => {
                                      const isMatch = searchTable && (athlete.name.toUpperCase().includes(searchTable.toUpperCase()) || String(athlete.subscriptionNumber).includes(searchTable));
                                      return (
                                      <AthleteChip
                                        key={athlete.subscriptionNumber}
                                        style={isMatch ? {
                                          backgroundColor: "#fef9c3",
                                          border: "1px solid #facc15",
                                          fontWeight: 600,
                                        } : undefined}
                                      >
                                        <span style={{ color: "#6b7490", fontSize: "11px", marginRight: 4 }}>#{athlete.subscriptionNumber}</span>
                                        {highlightText(athlete.name, searchTable)}
                                      </AthleteChip>
                                      );
                                    })}
                                  </AthletesGrid>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align="center"
                          sx={{ py: 4, color: "#9098b0" }}
                        >
                          {searchTable
                            ? "Nenhum resultado para a busca"
                            : "Nenhum dado encontrado"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Estado vazio */}
          {!reportData && !loading && (
            <Box
              sx={{
                mt: 6,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                color: "#9098b0",
              }}
            >
              <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
                Selecione os torneios e clique em Buscar para gerar o relatório
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default AthletesPerAcademy;
